import { useState, useEffect, useCallback, memo, useRef } from 'react';
import {
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorMode,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Tooltip,
  Flex,
  Icon,
  SimpleGrid,
  List,
  ListItem,
  ListIcon
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb,
  faCog,
  faSync,
  faChartLine,
  faNetworkWired,
  faHammer,
  faBolt,
  faExchangeAlt,
  faArrowUp,
  faArrowDown,
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle,
  faRobot,
  faQuoteLeft,
  faBookOpen,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import ApexCharts from 'apexcharts';
import { hasValidConfig, getEstimatedCost, getValidConfigs } from '../../utils/ai/aiProviders';
import { formatLargeNumber } from '../../utils/number.utils';
import { generateStructuredInsight, getMetricsSummary } from '../../utils/ai/aiService';
import { fmt, clean } from '../../utils/data/canonicalMetrics';
import { AIConfigModal } from './AIConfigModal';
import { getRandomSatoshiQuote } from '../../utils/satoshiQuotes';
import { useGlossary } from '../../contexts/GlossaryContext';

const MotionBox = motion(Box);

// Intro modal localStorage
const INTRO_KEY = 'ai_insights_intro_seen';
const INTRO_EXPIRY = 60 * 60 * 1000; // 1 hour

const shouldShowIntro = () => {
  try {
    const saved = localStorage.getItem(INTRO_KEY);
    if (!saved) return true;
    return Date.now() - parseInt(saved, 10) > INTRO_EXPIRY;
  } catch { return true; }
};

// Category configuration
const CATEGORIES = {
  market: { label: 'Market', icon: faChartLine, color: 'green' },
  network: { label: 'Network', icon: faNetworkWired, color: 'blue' },
  mining: { label: 'Mining', icon: faHammer, color: 'orange' },
  lightning: { label: 'Lightning', icon: faBolt, color: 'yellow' },
  mempool: { label: 'Mempool', icon: faExchangeAlt, color: 'cyan' }
};

const ALL_CATEGORIES = Object.keys(CATEGORIES);

// ---- Insight cache (module scope — pure of component state) ----
const CACHE_KEY = 'ai_insights_cache_v2';
const CACHE_DURATION = 30 * 60 * 1000;
const CACHE_MAX_ENTRIES = 10;
const CATEGORIES_KEY = 'ai_insights_selected_categories';

const makeCacheKey = (blockHeight, categories, currency) => {
  const cats = [...categories].sort().join(',') || 'none';
  return `${blockHeight ?? 'no-block'}|${cats}|${currency || 'usd'}`;
};

const loadCacheMap = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => v?.timestamp && now - v.timestamp < CACHE_DURATION)
    );
  } catch (e) {
    console.warn('[AIInsights] Failed to load cache:', e.message);
    return {};
  }
};

const saveCacheEntry = (key, entry) => {
  try {
    const map = loadCacheMap();
    map[key] = entry;
    const trimmed = Object.fromEntries(
      Object.entries(map)
        .sort(([, a], [, b]) => (b?.timestamp || 0) - (a?.timestamp || 0))
        .slice(0, CACHE_MAX_ENTRIES)
    );
    localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.warn('[AIInsights] Failed to save cache:', e.message);
  }
};

const loadSelectedCategories = () => {
  try {
    const raw = localStorage.getItem(CATEGORIES_KEY);
    if (!raw) return ALL_CATEGORIES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(k => ALL_CATEGORIES.includes(k))) {
      return parsed;
    }
  } catch (e) {
    console.warn('[AIInsights] Failed to load categories:', e.message);
  }
  return ALL_CATEGORIES;
};

// Format object keys for display (e.g., "MINOR_ISSUES" -> "MINOR ISSUES")
const formatKeyForDisplay = (key) => {
  if (!key || typeof key !== 'string') return key;
  return key.replace(/_/g, ' ');
};

// Alert icon and color mapping
const getAlertConfig = (type) => {
  switch (type) {
    case 'warning': return { icon: faExclamationTriangle, color: 'orange' };
    case 'success': return { icon: faCheckCircle, color: 'green' };
    default: return { icon: faInfoCircle, color: 'blue' };
  }
};

// Format metric value - preserve already-formatted values
const formatMetricValue = (value) => {
  if (!value) return 'N/A';
  if (typeof value !== 'string') return String(value);
  // Already has formatting (currency, percent, BTC, abbreviations) - keep as-is
  if (/[$%]|BTC|sat|[TBMK]\s*$|^\d+\.\d{8}/i.test(value)) return value;
  // Has commas = already formatted (e.g. block height "878,543") - keep as-is
  if (value.includes(',')) return value;
  // Plain large number without formatting - apply abbreviations
  const num = parseFloat(value);
  if (!isNaN(num) && num >= 1000000) {
    return formatLargeNumber(num, 2);
  }
  return value;
};

// Theme colors from theme.js
const THEME_COLORS = {
  orange: { 100: '#fae1b1', 300: '#ffc451', 500: '#f7931a', 700: '#be6500' },
  blue: { 100: '#dfedff', 300: '#b3d4fc', 400: '#4a96d4', 500: '#0d579b', 700: '#002f6c' },
  green: { 300: '#a5d6a7', 500: '#329239', 700: '#1b5e20' },
  red: { 300: '#ef9a9a', 500: '#c62828', 700: '#8e0000' },
  purple: { 300: '#ba68c8', 500: '#8B00FF', 700: '#6B238E' },
  gray: { 100: '#e0e0e0', 200: '#999999', 300: '#666666', 500: '#4d4d4d', 700: '#777777' },
  primary: { 300: '#272727', 500: '#1d1d1d', 700: '#121212' }
};


// Metric Card component - matches app's SingleDataValue styling
const MetricCard = memo(({ metric, colorMode }) => {
  if (!metric) return null;
  const { label, value, change, significance } = metric;
  const hasChange = change !== null && change !== undefined && Number.isFinite(change);
  const isPositive = hasChange && change >= 0;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const valueColor = colorMode === 'dark' ? '#ffffff' : '#121212';

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
      borderWidth={significance === 'high' ? '1px' : '0'}
      borderColor={significance === 'high' ? (colorMode === 'dark' ? THEME_COLORS.orange[500] : THEME_COLORS.blue[500]) : 'transparent'}
      textAlign="center"
    >
      <Text
        fontSize="13px"
        color={labelColor}
        fontWeight="700"
        textTransform="uppercase"
        lineHeight="1"
        mb={2}
      >
        {label}
      </Text>
      <HStack spacing={1} justify="center" align="center">
        <Text fontSize="20px" fontWeight="700" lineHeight="1" color={valueColor}>
          {formatMetricValue(value)}
        </Text>
        {hasChange && (
          <HStack spacing={0.5} color={isPositive ? THEME_COLORS.green[500] : THEME_COLORS.red[300]}>
            <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} size="xs" />
            <Text fontSize="12px" fontWeight="700">
              {clean(Math.abs(change))}%
            </Text>
          </HStack>
        )}
      </HStack>
    </Flex>
  );
});
MetricCard.displayName = 'MetricCard';

// Progress Bar component - uses theme colors
const InsightProgressBar = memo(({ label, progress, detail, colorMode }) => {
  const progressNum = parseFloat(progress) || 0;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const accentColor = THEME_COLORS.orange[500];

  return (
    <Box bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'} p={3} borderRadius="3px">
      <HStack justify="space-between" mb={2}>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color={labelColor} lineHeight="1">{label}</Text>
        <Text fontSize="14px" fontWeight="700" color={accentColor}>
          {clean(progressNum)}%
        </Text>
      </HStack>
      <Box
        h="6px"
        bg={colorMode === 'dark' ? THEME_COLORS.gray[500] : THEME_COLORS.gray[100]}
        borderRadius="3px"
        overflow="hidden"
      >
        <Box
          h="100%"
          w={`${Math.min(progressNum, 100)}%`}
          bg={accentColor}
          borderRadius="3px"
          transition="width 0.3s ease"
        />
      </Box>
      {detail && <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} mt={2}>{detail}</Text>}
    </Box>
  );
});
InsightProgressBar.displayName = 'InsightProgressBar';

// Alert Badge component - uses theme colors
const AlertBadge = memo(({ alert, colorMode }) => {
  if (!alert?.message) return null;
  const config = getAlertConfig(alert.type);
  const alertColors = {
    warning: { bg: colorMode === 'dark' ? '#3d2800' : THEME_COLORS.orange[100], border: THEME_COLORS.orange[500], icon: THEME_COLORS.orange[500] },
    success: { bg: colorMode === 'dark' ? '#0d2610' : '#e8f5e9', border: THEME_COLORS.green[500], icon: THEME_COLORS.green[500] },
    info: { bg: colorMode === 'dark' ? '#002f6c' : THEME_COLORS.gray[100], border: THEME_COLORS.blue[400], icon: THEME_COLORS.blue[400] }
  };
  const colors = alertColors[alert.type] || alertColors.info;

  return (
    <HStack
      spacing={2}
      py={2}
      px={3}
      bg={colors.bg}
      borderRadius="3px"
      width="fit-content"
    >
      <FontAwesomeIcon icon={config.icon} color={colors.icon} size="sm" />
      <Text fontSize="12px" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{alert.message}</Text>
    </HStack>
  );
});
AlertBadge.displayName = 'AlertBadge';

// Section Status component - uses theme colors
const SectionStatus = memo(({ category, status, highlight, colorMode }) => {
  const cfg = CATEGORIES[category];
  if (!cfg) return null;

  const categoryColors = {
    market: THEME_COLORS.green[500],
    network: THEME_COLORS.blue[400],
    mining: THEME_COLORS.orange[500],
    lightning: THEME_COLORS.orange[300],
    mempool: THEME_COLORS.blue[400]
  };

  const statusColorMap = {
    strong: THEME_COLORS.green[500], healthy: THEME_COLORS.green[500], profitable: THEME_COLORS.green[500], growing: THEME_COLORS.green[500], clear: THEME_COLORS.green[500],
    weak: THEME_COLORS.red[300], stressed: THEME_COLORS.red[300], challenging: THEME_COLORS.red[300], declining: THEME_COLORS.red[300], congested: THEME_COLORS.red[300],
    neutral: THEME_COLORS.gray[200], stable: THEME_COLORS.blue[400], normal: THEME_COLORS.blue[400], busy: THEME_COLORS.orange[500]
  };

  const accentColor = categoryColors[category] || THEME_COLORS.gray[200];
  const statusColor = statusColorMap[status?.toLowerCase()] || THEME_COLORS.gray[200];

  return (
    <Box
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
      h="100%"
    >
      <HStack justify="space-between" mb={highlight ? 2 : 0}>
        <HStack spacing={2}>
          <Icon as={FontAwesomeIcon} icon={cfg.icon} color={accentColor} boxSize="18px" />
          <Text fontSize="16px" fontWeight="700" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{cfg.label}</Text>
        </HStack>
        <Text
          fontSize="14px"
          fontWeight="700"
          textTransform="uppercase"
          color={statusColor}
          bg={colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
          px={2}
          py={1}
          borderRadius="3px"
        >
          {status}
        </Text>
      </HStack>
      {highlight && (
        <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} noOfLines={2} lineHeight="1.3">
          {highlight}
        </Text>
      )}
    </Box>
  );
});
SectionStatus.displayName = 'SectionStatus';

// Analytics Panel - uses theme colors, compact grid layout
const AnalyticsPanel = memo(({ analytics, colorMode }) => {
  if (!analytics) return null;

  const { trends, anomalies, correlations, benchmarks, marketCondition, flags } = analytics;
  const bgColor = colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff';
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const textColor = colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300];
  const textPrimary = colorMode === 'dark' ? '#ffffff' : '#121212';

  const sentimentColorMap = { BULLISH: THEME_COLORS.green[500], BEARISH: THEME_COLORS.red[300] };
  const sentimentBorderColor = sentimentColorMap[marketCondition?.sentiment] || THEME_COLORS.gray[200];
  const trendBorderColor = sentimentColorMap[trends?.price?.direction] || THEME_COLORS.gray[200];

  return (
    <VStack spacing={2} align="stretch">
      {/* Advanced Analytics: Market, Network, Trend, Benchmarks, Anomalies, Correlations */}
      <Flex flexWrap="wrap" gap={2}>
        {marketCondition && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Market</Text>
              <Text fontSize="12px" fontWeight="700" color={sentimentBorderColor} textTransform="uppercase">{marketCondition.sentiment}</Text>
            </HStack>
            <VStack align="stretch" spacing={1} fontSize="12px" color={textColor} lineHeight="1.3">
              {marketCondition.summary.split(/[;,]/).map((part, i) => {
                const trimmed = part.trim();
                return trimmed ? <Text key={i}>{trimmed}</Text> : null;
              })}
            </VStack>
          </Box>
        )}

        {flags?.networkHealth && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Network</Text>
              <Text fontSize="12px" fontWeight="700" textTransform="uppercase" color={flags.networkHealth === 'HEALTHY' ? THEME_COLORS.green[500] : flags.networkHealth === 'MINOR_ISSUES' ? THEME_COLORS.orange[500] : THEME_COLORS.red[300]}>
                {formatKeyForDisplay(flags.networkHealth)}
              </Text>
            </HStack>
            <VStack align="stretch" spacing={1} fontSize="12px" color={textColor} lineHeight="1.3">
              {benchmarks?.hashrate && (
                <Text>{benchmarks.hashrate.context}</Text>
              )}
              {benchmarks?.blockTime && (
                <Text>{benchmarks.blockTime.context}</Text>
              )}
            </VStack>
          </Box>
        )}

        {trends?.price && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack justify="space-between" mb={1}>
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Trend</Text>
              <Text fontSize="12px" fontWeight="700" color={trendBorderColor} textTransform="uppercase">
                {trends.price.strength !== 'NEUTRAL'
                  ? `${trends.price.strength} ${trends.price.direction}`
                  : trends.price.direction
                }
              </Text>
            </HStack>
            <VStack align="stretch" spacing={1} fontSize="14px" color={textColor} lineHeight="1.3">
              <HStack gap={2}>
                {trends.movingAverages?.price?.ma7 && <Text>7d: <Text as="span" fontWeight="700" color={textPrimary}>${clean(trends.movingAverages.price.ma7 / 1000)}k</Text></Text>}
                {trends.movingAverages?.price?.ma30 && <Text>30d: <Text as="span" fontWeight="700" color={textPrimary}>${clean(trends.movingAverages.price.ma30 / 1000)}k</Text></Text>}
              </HStack>
              {trends.movingAverages?.price?.currentVsMa7 != null && (
                <Text>vs 7d: <Text as="span" fontWeight="700" color={trends.movingAverages.price.currentVsMa7 >= 0 ? THEME_COLORS.green[500] : THEME_COLORS.red[300]}>{trends.movingAverages.price.currentVsMa7 >= 0 ? '+' : ''}{clean(trends.movingAverages.price.currentVsMa7)}%</Text></Text>
              )}
            </VStack>
          </Box>
        )}

        {benchmarks && Object.values(benchmarks).some(b => b) && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor} mb={1}>Benchmarks</Text>
            <Flex flexWrap="wrap" gap={1}>
              {Object.entries(benchmarks).map(([key, b]) => {
                if (!b) return null;
                const keyLower = key.toLowerCase();
                const isSecurityMetric = ['hashrate', 'difficulty', 'nodes', 'nodecount'].includes(keyLower);
                const levelColorHex = (() => {
                  if (['CONGESTED', 'SLOW', 'EXTREME'].includes(b.level)) return THEME_COLORS.red[300];
                  if (['HIGH'].includes(b.level)) {
                    return isSecurityMetric ? THEME_COLORS.green[500] : THEME_COLORS.red[300];
                  }
                  if (['LOW', 'LIGHT'].includes(b.level)) {
                    return isSecurityMetric ? THEME_COLORS.orange[500] : THEME_COLORS.green[500];
                  }
                  return THEME_COLORS.green[500];
                })();
                return (
                  <Tooltip key={key} label={b.context} placement="top">
                    <Text fontSize="9px" fontWeight="700" color={levelColorHex} cursor="help" bg={colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} px={1} py={0.5} borderRadius="3px" textTransform="uppercase">
                      {formatKeyForDisplay(key)}: {formatKeyForDisplay(b.level)}
                    </Text>
                  </Tooltip>
                );
              })}
            </Flex>
          </Box>
        )}

        {anomalies && anomalies.length > 0 && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <HStack mb={1}>
              <FontAwesomeIcon icon={faExclamationTriangle} color={THEME_COLORS.orange[500]} size="xs" />
              <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor}>Anomalies ({anomalies.length})</Text>
            </HStack>
            <Flex flexWrap="wrap" gap={1}>
              {anomalies.slice(0, 3).map((anomaly, idx) => (
                <Tooltip key={idx} label={anomaly.message} placement="top">
                  <Text fontSize="9px" fontWeight="700" cursor="help" color={anomaly.severity === 'CRITICAL' ? THEME_COLORS.red[300] : THEME_COLORS.orange[500]} bg={colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} px={1} py={0.5} borderRadius="3px" textTransform="uppercase">
                    {formatKeyForDisplay(anomaly.metric)}: {formatKeyForDisplay(anomaly.direction || anomaly.severity)}
                  </Text>
                </Tooltip>
              ))}
            </Flex>
          </Box>
        )}

        {correlations && (correlations.priceHashrate?.value != null || correlations.feeMempool?.value != null) && (
          <Box bg={bgColor} p={3} borderRadius="3px" flex="1" minW="180px">
            <Text fontSize="13px" fontWeight="700" textTransform="uppercase" color={labelColor} mb={1}>Correlations</Text>
            <Flex fontSize="13px" color={textColor} gap={4} flexWrap="wrap">
              {correlations.priceHashrate?.value != null && (
                <Tooltip label={correlations.priceHashrate.interpretation} placement="top">
                  <Text cursor="help">Price-Hash: <Text as="span" fontWeight="700" color={correlations.priceHashrate.value > 0.5 ? THEME_COLORS.green[500] : THEME_COLORS.gray[200]}>{clean(correlations.priceHashrate.value, 2)}</Text></Text>
                </Tooltip>
              )}
              {correlations.feeMempool?.value != null && (
                <Tooltip label={correlations.feeMempool.interpretation} placement="top">
                  <Text cursor="help">Fee-Mempool: <Text as="span" fontWeight="700" color={correlations.feeMempool.value > 0.5 ? THEME_COLORS.green[500] : THEME_COLORS.gray[200]}>{clean(correlations.feeMempool.value, 2)}</Text></Text>
                </Tooltip>
              )}
            </Flex>
          </Box>
        )}
      </Flex>
    </VStack>
  );
});
AnalyticsPanel.displayName = 'AnalyticsPanel';

// Satoshi Quote Flair - uses theme colors
const SatoshiQuoteFlair = memo(({ colorMode }) => {
  const [quoteObj] = useState(() => getRandomSatoshiQuote());

  return (
    <Box
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
    >
      <HStack spacing={2} mb={2}>
        <FontAwesomeIcon icon={faQuoteLeft} color={THEME_COLORS.orange[500]} size="sm" />
        <Text fontSize="11px" fontWeight="700" color={THEME_COLORS.orange[500]} textTransform="uppercase">
          Satoshi Nakamoto
        </Text>
      </HStack>
      <Text fontSize="14px" fontStyle="italic" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} lineHeight="1.5">
        {quoteObj.quote}
      </Text>
    </Box>
  );
});
SatoshiQuoteFlair.displayName = 'SatoshiQuoteFlair';

// BTCLexicon Term Flair - uses theme colors
const LexiconTermFlair = memo(({ colorMode }) => {
  const { glossaryData, loadGlossary, isLoaded } = useGlossary();
  const [term, setTerm] = useState(null);

  useEffect(() => {
    if (!isLoaded) {
      loadGlossary();
    }
  }, [isLoaded, loadGlossary]);

  useEffect(() => {
    if (glossaryData && typeof glossaryData === 'object') {
      const allTerms = Object.entries(glossaryData)
        .filter(([category]) => category !== 'version')
        .flatMap(([, terms]) => Object.values(terms))
        .filter(t => t && t.title && t.definition);

      if (allTerms.length > 0) {
        const randomTerm = allTerms[Math.floor(Math.random() * allTerms.length)];
        setTerm(randomTerm);
      }
    }
  }, [glossaryData]);

  if (!term) return null;

  return (
    <Box
      bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'}
      p={3}
      borderRadius="3px"
    >
      <HStack spacing={2} mb={2}>
        <FontAwesomeIcon icon={faBookOpen} color={THEME_COLORS.orange[500]} size="sm" />
        <Text fontSize="11px" fontWeight="700" color={THEME_COLORS.orange[500]} textTransform="uppercase">
          BTC Lexicon
        </Text>
      </HStack>
      <Text fontSize="15px" fontWeight="700" mb={1} color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{term.title}</Text>
      <Text fontSize="14px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} lineHeight="1.5">
        {term.definition}
      </Text>
    </Box>
  );
});
LexiconTermFlair.displayName = 'LexiconTermFlair';

// Mini Price Chart - uses theme colors
const MiniPriceChart = memo(({ data, colorMode, compact = false, canonicalChange30d }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const chartHeight = compact ? 60 : 100;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];

  useEffect(() => {
    if (!data?.ts15?.price?.values || !chartRef.current) return;

    const priceData = data.ts15.price.values;
    const recentData = priceData.slice(-30).map(p => ({
      x: new Date(p.x * 1000),
      y: p.y
    }));

    if (recentData.length < 2) return;

    const firstPrice = recentData[0].y;
    const lastPrice = recentData[recentData.length - 1].y;
    const isPositive = lastPrice >= firstPrice;
    const chartColor = isPositive ? THEME_COLORS.green[500] : THEME_COLORS.red[500];

    const options = {
      chart: {
        type: 'area',
        height: chartHeight,
        sparkline: { enabled: true },
        animations: { enabled: false }
      },
      series: [{ name: 'Price', data: recentData }],
      stroke: { curve: 'smooth', width: compact ? 1.5 : 2 },
      colors: [chartColor],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 100]
        }
      },
      tooltip: {
        enabled: !compact,
        theme: colorMode,
        x: { format: 'MMM dd' },
        y: { formatter: (val) => `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}` }
      }
    };

    if (chartInstance.current) {
      chartInstance.current.updateOptions(options, false, false, false);
    } else {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, colorMode, chartHeight, compact]);

  if (!data?.ts15?.price?.values) return null;

  const priceData = data.ts15.price.values;
  const recentData = priceData.slice(-30);
  if (recentData.length < 2) return null;

  const firstPrice = recentData[0].y;
  const lastPrice = recentData[recentData.length - 1].y;
  // Use canonical metrics as single source of truth for 30d change, fall back to chart calculation
  const change = canonicalChange30d != null ? canonicalChange30d : ((lastPrice - firstPrice) / firstPrice) * 100;
  const isPositive = change >= 0;
  const changeColor = isPositive ? THEME_COLORS.green[500] : THEME_COLORS.red[500];

  if (compact) {
    return (
      <VStack spacing={1} align="stretch" h="100%">
        <HStack justify="space-between">
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color={labelColor}>30d Trend</Text>
          <HStack spacing={1}>
            <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} size="xs" color={changeColor} />
            <Text fontSize="12px" fontWeight="700" color={changeColor}>{clean(Math.abs(change))}%</Text>
          </HStack>
        </HStack>
        <Box ref={chartRef} height={`${chartHeight}px`} flex={1} />
      </VStack>
    );
  }

  return (
    <VStack spacing={2} align="stretch">
      <HStack justify="space-between">
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color={labelColor}>30-Day Price Trend</Text>
        <HStack spacing={1}>
          <FontAwesomeIcon icon={isPositive ? faArrowUp : faArrowDown} size="xs" color={changeColor} />
          <Text fontSize="14px" fontWeight="700" color={changeColor}>{clean(Math.abs(change))}%</Text>
        </HStack>
      </HStack>
      <Box ref={chartRef} height={`${chartHeight}px`} />
    </VStack>
  );
});
MiniPriceChart.displayName = 'MiniPriceChart';

// Unified Dashboard Panel - uses theme colors
const UnifiedDashboard = memo(({ insight, colorMode, data, bitcoinPrice, metrics: m }) => {
  if (!insight) return null;

  const { headline, overallSentiment, sentiment, marketScore, sections, alerts, insights: textInsights, outlook } = insight;
  const displaySentiment = sentiment || overallSentiment;
  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const bgCard = colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff';
  const textPrimary = colorMode === 'dark' ? '#ffffff' : '#121212';

  const sentimentColorHex = displaySentiment?.toLowerCase() === 'bullish' ? THEME_COLORS.green[500]
    : displaySentiment?.toLowerCase() === 'bearish' ? THEME_COLORS.red[300] : THEME_COLORS.gray[200];
  const scoreColor = marketScore >= 7 ? THEME_COLORS.green[500] : marketScore >= 4 ? THEME_COLORS.orange[500] : THEME_COLORS.red[300];

  // Calculate ATH distance from real data
  const ath = data?.s06?.bitcoin?.market_data?.ath?.usd;
  const price = parseFloat(bitcoinPrice);
  const athDistance = (ath && price) ? ((price - ath) / ath * 100) : null;

  // Fix incorrect ATH references in AI-generated text (post-processing guard)
  const fixAthReferences = (text) => {
    if (!text || athDistance == null) return text;
    const realPct = Math.abs(athDistance).toFixed(0);
    return text.replace(
      /(\d{1,3}(?:\.\d+)?)\s*%\s*(?:from|below|off|under)\s*(?:ATH|all[- ]time high|the all[- ]time (?:high|peak))/gi,
      `${realPct}% below ATH`
    );
  };

  // Replace ALL tildes with "about" in analysis/watch text — eliminates garbled ~1.~1 forever
  const replaceTildes = (text) => {
    if (!text) return text;
    return text
      // Fix garbled tildes mid-number FIRST: "~1.~1" → "1.1"
      .replace(/~(\d+)\.~(\d+)/g, '$1.$2')
      // Fix any tilde-between-digits: "~1~1" → "11"
      .replace(/~(\d[\d.]*)~(\d)/g, '$1$2')
      // Replace "~number" with "about number" (e.g. "~1.1 ZH/s" → "about 1.1 ZH/s")
      .replace(/~(\d)/g, 'about $1');
  };

  // Capitalize first letter of a string
  const capitalizeFirst = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  // Compose all AI text post-processors — applied to Analysis and Watch blurbs
  const fixAIText = (text) => capitalizeFirst(replaceTildes(fixAthReferences(text)));

  const displayHeadline = fixAIText(
    bitcoinPrice && headline
      ? headline.replace(/\$[\d,]+(\.\d{1,2})?[BMTK]?/, `$${parseFloat(bitcoinPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
      : headline
  );

  // Override ALL AI section highlights with real data from metrics (single source of truth)
  const realHighlights = {
    market: [
      m?.price?.change30d != null ? `${m.price.change30d >= 0 ? 'Up' : 'Down'} ${fmt.pct(Math.abs(m.price.change30d))} (30d)` : null,
      athDistance != null ? `${fmt.pct(Math.abs(athDistance))} below ATH` : null,
    ].filter(Boolean).join(', '),
    network: [
      m?.network?.hashrate ? `${fmt.hashrate(m.network.hashrate)} hashrate` : null,
      insight?.analytics?.flags?.networkHealth ? formatKeyForDisplay(insight.analytics.flags.networkHealth).toLowerCase() : null,
    ].filter(Boolean).join(', '),
    mining: [
      insight?.analytics?.benchmarks?.fees ? `Fees ${insight.analytics.benchmarks.fees.level.toLowerCase()}` : null,
      insight?.analytics?.benchmarks?.fees?.context || null,
      // Fallback: fee/subsidy ratio from metrics if benchmarks unavailable
      !insight?.analytics?.benchmarks?.fees && m?.mining?.feeSubsidyRatio != null
        ? `Fee/subsidy ratio: ${fmt.pct(m.mining.feeSubsidyRatio, 2)}`
        : null,
      // Last resort fallback — derive from metrics dynamically
      !insight?.analytics?.benchmarks?.fees && m?.mining?.feeSubsidyRatio == null
        ? `${m?.mining?.currentReward ? (m.mining.currentReward / 1e8).toFixed(4) : '3.125'} BTC subsidy${m?.mining?.currentEpoch ? ` (Epoch ${m.mining.currentEpoch})` : ''}`
        : null,
    ].filter(Boolean).join(', '),
    lightning: [
      m?.lightning?.channels ? `${fmt.num(m.lightning.channels)} channels` : null,
      m?.lightning?.capacity ? `${fmt.btcShort(m.lightning.capacity)} BTC capacity` : null,
    ].filter(Boolean).join(', '),
    mempool: [
      m?.activity?.mempoolTxCount ? `${fmt.num(m.activity.mempoolTxCount)} unconfirmed txs` : null,
      m?.activity?.mempoolUsage && m?.activity?.mempoolMaxSize
        ? `${((m.activity.mempoolUsage / m.activity.mempoolMaxSize) * 100).toFixed(0)}% full`
        : null,
    ].filter(Boolean).join(', '),
  };

  const filteredSections = sections
    ? Object.entries(sections).map(([key, sectionData]) =>
        realHighlights[key]
          ? [key, { ...sectionData, highlight: realHighlights[key] }]
          : [key, { ...sectionData, highlight: fixAIText(sectionData.highlight) }]
      )
    : [];

  return (
    <VStack align="stretch" spacing={3}>
      {/* Headline Row */}
      <Flex gap={3} direction={{ base: 'column', lg: 'row' }}>
        {displayHeadline && (
          <Box bg={bgCard} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 60%' }}>
            <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2} mb={2}>
              <Text fontSize={{ base: '18px', md: '22px' }} fontWeight="700" lineHeight="1.3" color={textPrimary} flex={1}>
                {displayHeadline}
              </Text>
              {displaySentiment && (
                <Text fontSize="12px" fontWeight="700" color={sentimentColorHex} textTransform="uppercase" bg={colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} px={3} py={1} borderRadius="3px">
                  {displaySentiment.toUpperCase()}
                </Text>
              )}
            </HStack>
            {(marketScore || athDistance != null) && (
              <HStack spacing={4} mb={alerts?.length > 0 ? 3 : 0} flexWrap="wrap">
                {marketScore && (
                  <HStack spacing={2}>
                    <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}>Market Score:</Text>
                    <Text fontSize="14px" fontWeight="700" color={scoreColor}>{marketScore}/10</Text>
                    <Tooltip label="Considers: price trend, ATH distance, hashrate, fees, Lightning, mempool" placement="bottom" hasArrow bg={colorMode === 'dark' ? THEME_COLORS.primary[500] : '#f5f5f5'} color={textPrimary}>
                      <Box as="span" cursor="help" ml={1}><FontAwesomeIcon icon={faInfoCircle} size="xs" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} /></Box>
                    </Tooltip>
                  </HStack>
                )}
                {athDistance != null && (
                  <HStack spacing={2}>
                    <Text fontSize="12px" color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}>From ATH:</Text>
                    <Text fontSize="14px" fontWeight="700" color={athDistance >= 0 ? THEME_COLORS.green[500] : THEME_COLORS.red[300]}>{athDistance >= 0 ? '+' : ''}{clean(athDistance)}%</Text>
                  </HStack>
                )}
              </HStack>
            )}
            {alerts && alerts.length > 0 && <Flex flexWrap="wrap" gap={2}>{alerts.map((alert, idx) => <AlertBadge key={idx} alert={{ ...alert, message: fixAIText(alert.message) }} colorMode={colorMode} />)}</Flex>}
          </Box>
        )}
        {data && (
          <Box bg={bgCard} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 40%' }}>
            <MiniPriceChart data={data} colorMode={colorMode} canonicalChange30d={m?.price?.change30d} />
          </Box>
        )}
      </Flex>

      {/* Section Status Cards */}
      {filteredSections.length > 0 && (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: filteredSections.length }} spacing={2}>
          {filteredSections.map(([key, sectionData]) => (
            <SectionStatus key={key} category={key} status={sectionData.status} highlight={sectionData.highlight} colorMode={colorMode} />
          ))}
        </SimpleGrid>
      )}

      {/* Analysis & Outlook */}
      {(textInsights?.length > 0 || outlook) && (
        <Flex flexWrap="wrap" gap={3}>
          {textInsights && textInsights.length > 0 && (
            <Box bg={bgCard} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 calc(60% - 6px)' }} minW="250px">
              <Text fontSize="12px" color={labelColor} fontWeight="700" textTransform="uppercase" mb={3}>Analysis</Text>
              <VStack align="stretch" spacing={2}>
                {textInsights.slice(0, 4).map((text, idx) => (
                  <HStack key={idx} align="flex-start" spacing={2}>
                    <Text color={THEME_COLORS.orange[500]} fontSize="13px" mt="1px">•</Text>
                    <Text fontSize="13px" lineHeight="1.45" color={textPrimary}>{fixAIText(text)}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}
          {outlook && (
            <Box bg={colorMode === 'dark' ? '#3d2800' : THEME_COLORS.orange[100]} p={4} borderRadius="3px" flex={{ base: '1 1 100%', lg: '1 1 calc(40% - 6px)' }} minW="180px">
              <Text fontSize="11px" fontWeight="700" color={THEME_COLORS.orange[500]} textTransform="uppercase" mb={2}>Watch</Text>
              {Array.isArray(outlook) ? (
                <VStack align="stretch" spacing={2}>
                  {outlook.slice(0, 3).map((item, idx) => (
                    <HStack key={idx} align="flex-start" spacing={2}>
                      <Text color={THEME_COLORS.orange[500]} fontSize="13px" mt="1px">•</Text>
                      <Text fontSize="13px" lineHeight="1.45" color={textPrimary}>{fixAIText(item)}</Text>
                    </HStack>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="13px" lineHeight="1.45" color={textPrimary}>{fixAIText(outlook)}</Text>
              )}
            </Box>
          )}
        </Flex>
      )}
    </VStack>
  );
});
UnifiedDashboard.displayName = 'UnifiedDashboard';

// Quick Stats - uses theme colors, compact grid matching app design
const QuickStats = ({ metrics, colorMode }) => {
  if (!metrics) return null;

  const labelColor = colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500];
  const quickMetrics = [
    { label: 'Block Height', value: metrics.network?.blockHeight ? fmt.num(metrics.network.blockHeight) : null },
    { label: 'Hashrate', value: metrics.network?.hashrate ? fmt.hashrate(metrics.network.hashrate) : null },
    { label: 'Difficulty', value: metrics.network?.difficulty ? fmt.difficulty(metrics.network.difficulty) : null },
    { label: 'Mempool', value: metrics.activity?.mempoolTxCount ? `${fmt.num(metrics.activity.mempoolTxCount)} TX` : null },
    { label: 'Fastest Fee', value: metrics.mining?.feeRates?.fastestFee ? `${metrics.mining.feeRates.fastestFee} sat/vB` : null },
    { label: 'Txs 24h', value: metrics.activity?.transactions24h ? formatLargeNumber(metrics.activity.transactions24h, 1) : null },
    { label: 'Active Addrs', value: metrics.activity?.activeAddresses ? formatLargeNumber(metrics.activity.activeAddresses, 1) : null },
    { label: 'LN Nodes', value: metrics.lightning?.nodes ? fmt.num(metrics.lightning.nodes) : null },
    { label: 'LN BTC Capacity', value: metrics.lightning?.capacity ? fmt.btcShort(metrics.lightning.capacity) : null },
    { label: 'Halving', value: metrics.mining?.halvingProgress ? `${metrics.mining.halvingProgress}%` : null }
  ];

  return (
    <Box mt={4}>
      <Text fontSize="13px" color={labelColor} mb={2} fontWeight="700" textTransform="uppercase">Key Metrics</Text>
      <Flex flexWrap="wrap" gap={2}>
        {quickMetrics.filter(m => m.value).map((m, idx) => (
          <Box key={idx} flex={{ base: '1 1 calc(50% - 4px)', md: '1 1 calc(20% - 6.4px)' }} minW="120px">
            <MetricCard metric={{ ...m, significance: 'low' }} colorMode={colorMode} />
          </Box>
        ))}
      </Flex>
    </Box>
  );
};

// Main Component
export const AIInsightsButton = ({
  data,
  bitcoinPrice,
  lastMempoolBlockData,
  fees,
  da,
  mempoolInfo,
  diamondbackData,
  blockMetrics
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfigOpen, onOpen: onConfigOpen, onClose: onConfigClose } = useDisclosure();
  const { colorMode } = useColorMode();

  const [selectedCategories, setSelectedCategories] = useState(loadSelectedCategories);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [cachedBlockHeight, setCachedBlockHeight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingLog, setLoadingLog] = useState([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState(null);
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [estimatedCost, setEstimatedCost] = useState(() => getEstimatedCost());
  const [showIntro, setShowIntro] = useState(false);
  const loadStartRef = useRef(null);

  const dismissIntro = () => {
    setShowIntro(false);
    localStorage.setItem(INTRO_KEY, String(Date.now()));
  };

  // Progress callback from generateStructuredInsight
  const handleProgress = useCallback(({ step, detail }) => {
    setLoadingLog(prev => {
      if (prev.length > 0 && prev[prev.length - 1].step === step) return prev;
      return [...prev, { step, detail, ts: Date.now() }];
    });
  }, []);

  // Elapsed timer while loading
  useEffect(() => {
    if (!loading) { setElapsedMs(0); return; }
    loadStartRef.current = Date.now();
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - loadStartRef.current);
    }, 100);
    return () => clearInterval(timer);
  }, [loading]);

  // Restore most recent cached insight on mount (regardless of block/categories)
  // Staleness is surfaced via the footer indicator, not by hiding the insight.
  useEffect(() => {
    const map = loadCacheMap();
    const entries = Object.entries(map).sort(([, a], [, b]) => (b?.timestamp || 0) - (a?.timestamp || 0));
    if (entries.length > 0 && entries[0][1]?.insight) {
      const entry = entries[0][1];
      setCurrentInsight(entry.insight);
      setCachedBlockHeight(entry.blockHeight ?? null);
    }
  }, []);

  // Persist category selection across sessions
  useEffect(() => {
    try {
      localStorage.setItem(CATEGORIES_KEY, JSON.stringify(selectedCategories));
    } catch (e) {
      console.warn('[AIInsights] Failed to save categories:', e.message);
    }
  }, [selectedCategories]);

  const bgColor = colorMode === 'dark' ? '#121212' : '#f5f5f5';
  const secondaryText = colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300];

  const updateProviderInfo = () => {
    setEstimatedCost(getEstimatedCost());
  };

  const getAllData = useCallback(() => ({
    ...data,
    realtime: {
      bitcoinPrice,
      lastMempoolBlockData,
      fees,
      da,
      mempoolInfo,
      diamondbackData,
      blockMetrics
    }
  }), [data, bitcoinPrice, lastMempoolBlockData, fees, da, mempoolInfo, diamondbackData, blockMetrics]);

  useEffect(() => {
    if (data) {
      const allData = getAllData();
      setMetrics(getMetricsSummary(allData));
    }
  }, [data, getAllData]);

  useEffect(() => {
    const handleScroll = () => {
      setScrollToTopVisible(window.pageYOffset > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const bottomPosition = scrollToTopVisible ? '8rem' : '4.5rem';

  const handleButtonClick = () => {
    if (!hasValidConfig()) {
      onConfigOpen();
    } else {
      updateProviderInfo();
      if (shouldShowIntro()) setShowIntro(true);
      onOpen();
    }
  };

  const fetchInsight = async () => {
    if (!data) {
      setError('No data available. Please wait for data to load.');
      return;
    }

    setLoading(true);
    setLoadingLog([]);
    setError(null);

    try {
      const allData = getAllData();
      const result = await generateStructuredInsight('overview', allData, selectedCategories, handleProgress);

      const newInsight = {
        structured: result.type === 'structured',
        data: result.data,
        provider: result.provider,
        model: result.model,
        usedFallback: result.usedFallback,
        analytics: result.analytics
      };

      const blockHeight = lastMempoolBlockData?.height ?? null;
      const currency = (data?.selectedCurrency || 'usd').toLowerCase();
      setCurrentInsight(newInsight);
      setCachedBlockHeight(blockHeight);
      saveCacheEntry(makeCacheKey(blockHeight, selectedCategories, currency), {
        insight: newInsight,
        blockHeight,
        timestamp: Date.now()
      });
    } catch (err) {
      console.warn('[AIInsights] Generation failed:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchInsight();
  };

  const toggleCategory = (key) => {
    setSelectedCategories(prev => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter(k => k !== key);
      }
      return [...prev, key];
    });
  };

  const handleConfigured = () => {
    updateProviderInfo();
    onOpen();
  };

  const isConfigured = hasValidConfig();
  const currentBlockHeight = lastMempoolBlockData?.height ?? null;
  const blocksStale = cachedBlockHeight != null && currentBlockHeight != null && currentBlockHeight > cachedBlockHeight
    ? currentBlockHeight - cachedBlockHeight
    : 0;

  return (
    <>
      <MotionBox
        position="fixed"
        bottom={bottomPosition}
        right="1.2rem"
        zIndex={99}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut', delay: 1 }}
        style={{ transition: 'bottom 0.3s ease' }}
      >
        <Tooltip label={isConfigured ? 'AI Insights' : 'Configure AI Provider'} placement="left">
          <IconButton
            onClick={handleButtonClick}
            aria-label="AI Insights"
            icon={<FontAwesomeIcon icon={isConfigured ? faRobot : faCog} />}
            bg="#8B00FF"
            color="#ffffff"
            _hover={{ bg: "#ba68c8" }}
            size="md"
            borderRadius="3px"
          />
        </Tooltip>
      </MotionBox>

      <AIConfigModal
        isOpen={isConfigOpen}
        onClose={onConfigClose}
        onConfigured={handleConfigured}
      />

      <Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
        <ModalOverlay backgroundColor="rgba(16, 22, 26, 0.95)" />
        <ModalContent
          bg={bgColor}
          borderRadius="0"
          m={0}
          maxH="100vh"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader
            borderBottomWidth="1px"
            borderColor={colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
            py={3}
            flexShrink={0}
          >
            <Flex justify="space-between" align="center" pr={8} flexWrap="wrap" gap={2}>
              <HStack spacing={3}>
                <HStack spacing="5px">
                  <Box bg={THEME_COLORS.gray[500]} w="32px" h="32px" borderRadius="3px" />
                  <Box bg={THEME_COLORS.gray[500]} w="32px" h="32px" borderRadius="3px" />
                  <Box
                    bg={THEME_COLORS.orange[500]}
                    p={2}
                    borderRadius="3px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <FontAwesomeIcon icon={faRobot} color="#ffffff" size="lg" />
                  </Box>
                </HStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="18px" fontWeight="700" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>Timechain Stats AI</Text>
                  <Text fontSize="11px" fontWeight="700" color={colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500]} textTransform="uppercase">Bitcoin Analysis</Text>
                </VStack>
              </HStack>
              <HStack spacing={2}>
                <Tooltip label="Refresh analysis">
                  <IconButton
                    aria-label="Refresh"
                    icon={<FontAwesomeIcon icon={faSync} spin={loading} />}
                    size="sm"
                    variant="ghost"
                    onClick={handleRefresh}
                    isDisabled={loading}
                    color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}
                    _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                  />
                </Tooltip>
                <Tooltip label="AI provider settings">
                  <IconButton
                    aria-label="Settings"
                    icon={<FontAwesomeIcon icon={faCog} />}
                    size="sm"
                    variant="ghost"
                    onClick={onConfigOpen}
                    color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}
                    _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton
            color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]}
            _hover={{ bg: colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
          />
          <ModalBody py={4} px={{ base: 4, md: 6, lg: 8 }} flex={1} overflow="auto">
            <Flex
              direction={{ base: 'column', xl: 'row' }}
              gap={4}
              maxW="1800px"
              mx="auto"
              h="100%"
            >
              {/* Main Content */}
              <Box flex={1} minW={0} display="flex" flexDirection="column">

                {/* Category filter chips — always visible */}
                <Flex wrap="wrap" gap={2} mb={3} align="center">
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    textTransform="uppercase"
                    color={secondaryText}
                    letterSpacing="0.5px"
                    mr={1}
                  >
                    Filter
                  </Text>
                  {ALL_CATEGORIES.map(key => {
                    const cat = CATEGORIES[key];
                    const isActive = selectedCategories.includes(key);
                    const inactiveBorder = colorMode === 'dark' ? THEME_COLORS.gray[500] : THEME_COLORS.gray[200];
                    const inactiveColor = colorMode === 'dark' ? THEME_COLORS.gray[100] : THEME_COLORS.gray[500];
                    return (
                      <Button
                        key={key}
                        size="xs"
                        bg={isActive ? THEME_COLORS.orange[500] : 'transparent'}
                        color={isActive ? '#ffffff' : inactiveColor}
                        borderWidth="1px"
                        borderColor={isActive ? THEME_COLORS.orange[500] : inactiveBorder}
                        borderRadius="3px"
                        leftIcon={<FontAwesomeIcon icon={cat.icon} />}
                        onClick={() => toggleCategory(key)}
                        isDisabled={loading}
                        _hover={{ bg: isActive ? THEME_COLORS.orange[300] : (colorMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)') }}
                      >
                        {cat.label}
                      </Button>
                    );
                  })}
                </Flex>

                {/* Loading State — Cypherpunk Terminal */}
                {loading && (
                  <Flex direction="column" align="center" justify="center" flex={1} py={6}>
                    <Box
                      w="100%" maxW="560px"
                      bg={colorMode === 'dark' ? '#0a0e0a' : '#1a1a2e'}
                      border="1px solid"
                      borderColor={colorMode === 'dark' ? '#2a5a2a' : '#2a2a4e'}
                      borderRadius="6px"
                      overflow="hidden"
                      fontFamily="'Courier New', Courier, monospace"
                    >
                      {/* Terminal header bar */}
                      <HStack
                        px={4} py={2}
                        bg={colorMode === 'dark' ? '#0f1a0f' : '#12122e'}
                        borderBottom="1px solid"
                        borderColor={colorMode === 'dark' ? '#2a5a2a' : '#2a2a4e'}
                        justify="space-between"
                      >
                        <HStack spacing={1.5}>
                          <Box w="10px" h="10px" borderRadius="full" bg="#ff5f56" />
                          <Box w="10px" h="10px" borderRadius="full" bg="#ffbd2e" />
                          <Box w="10px" h="10px" borderRadius="full" bg="#27c93f" />
                        </HStack>
                        <Text fontSize="12px" color="#8ac88a" letterSpacing="1px" fontWeight="600">
                          satoshi@timechain ~
                        </Text>
                        <Text fontSize="11px" color="#6aaa6a" fontWeight="600">
                          {(elapsedMs / 1000).toFixed(1)}s
                        </Text>
                      </HStack>

                      {/* Terminal body */}
                      <Box px={4} py={4} minH="180px">
                        {/* Static init line */}
                        <Text fontSize="13px" color="#6aaa6a" mb={2}>
                          $ ./analyze-timechain --depth=full
                        </Text>

                        {/* Real progress log lines */}
                        {loadingLog.map((entry, i) => (
                          <HStack key={i} spacing={2} mb={1} align="baseline">
                            <Text fontSize="11px" color="#6aaa6a" flexShrink={0}>
                              [{entry.ts ? new Date(entry.ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '...'}]
                            </Text>
                            <Text fontSize="13px" color={i === loadingLog.length - 1 ? '#7aff7a' : '#8ac88a'}>
                              {entry.detail}
                            </Text>
                          </HStack>
                        ))}

                        {/* Blinking cursor on current step */}
                        <HStack spacing={0} mt={2}>
                          <Text fontSize="13px" color="#7aff7a" fontWeight="700">{'>'}</Text>
                          <Box
                            w="8px" h="15px" ml={1}
                            bg="#7aff7a"
                            animation="blink 1s step-end infinite"
                            sx={{ '@keyframes blink': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0 } } }}
                          />
                        </HStack>

                        {/* Hex decoration — subtle scrolling hash */}
                        <Text
                          fontSize="10px" color="#2a5a2a" mt={4}
                          letterSpacing="1px" wordBreak="break-all"
                          opacity={0.5}
                        >
                          {Array.from({ length: 48 }, (_, i) => '0123456789abcdef'[(Date.now() + i * 7) % 16]).join('')}
                        </Text>
                      </Box>
                    </Box>

                    {/* Provider info below terminal */}
                    <Text fontSize="13px" color={THEME_COLORS.orange[500]} mt={4} letterSpacing="0.5px" fontWeight="600">
                      Querying {loadingLog.find(l => l.step === 'generating')?.detail?.replace('Querying ', '')?.replace('...', '') || 'AI provider'}
                    </Text>
                  </Flex>
                )}

                {/* Error State */}
                {error && !loading && (
                  <Flex direction="column" align="center" justify="center" flex={1} py={8}>
                    <Text color={THEME_COLORS.red[300]} mb={4} fontSize="14px" whiteSpace="pre-line" textAlign="center">{error}</Text>
                    <Button size="sm" bg={THEME_COLORS.orange[500]} color="#ffffff" _hover={{ bg: THEME_COLORS.orange[300] }} onClick={fetchInsight}>
                      Try Again
                    </Button>
                  </Flex>
                )}

                {/* Insight Dashboard */}
                {currentInsight && !loading && (
                  <VStack align="stretch" spacing={3}>
                    {currentInsight.structured ? (
                      <UnifiedDashboard
                        insight={currentInsight.data}
                        colorMode={colorMode}
                        data={data}
                        bitcoinPrice={bitcoinPrice}
                        metrics={metrics}
                      />
                    ) : (
                      <Box bg={colorMode === 'dark' ? THEME_COLORS.primary[300] : '#ffffff'} p={4} borderRadius="3px">
                        <Text fontSize="13px" lineHeight="1.7" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>{currentInsight.data}</Text>
                      </Box>
                    )}
                    {/* Advanced Analytics - in main pane */}
                    {currentInsight?.analytics && (
                      <Box>
                        <Text fontSize="11px" color={colorMode === 'dark' ? THEME_COLORS.orange[700] : THEME_COLORS.blue[500]} fontWeight="700" textTransform="uppercase" mb={2}>
                          Advanced Analytics
                        </Text>
                        <AnalyticsPanel analytics={currentInsight.analytics} colorMode={colorMode} />
                      </Box>
                    )}
                    {/* QuickStats at bottom - only shows after AI is called */}
                    <QuickStats metrics={metrics} colorMode={colorMode} />
                  </VStack>
                )}

                {/* Generate Button (when no insight) */}
                {!currentInsight && !loading && !error && (
                  <Flex direction="column" align="center" justify="center" flex={1} py={8}>
                    <Icon as={FontAwesomeIcon} icon={faGlobe} boxSize={10} color={THEME_COLORS.orange[500]} mb={4} />
                    <Text color={secondaryText} fontSize="14px" mb={4} textAlign="center">
                      Generate AI-powered analysis of the Bitcoin network
                    </Text>
                    <Button
                      bg={THEME_COLORS.orange[500]}
                      color="#ffffff"
                      _hover={{ bg: THEME_COLORS.orange[300] }}
                      size="lg"
                      onClick={fetchInsight}
                      leftIcon={<FontAwesomeIcon icon={faLightbulb} />}
                    >
                      Analyze Network
                    </Button>
                    {estimatedCost && (
                      <Text fontSize="11px" color={estimatedCost.isFree ? THEME_COLORS.green[500] : secondaryText} mt={2}>
                        ~{((estimatedCost.inputTokens || 0) + (estimatedCost.outputTokens || 0)).toLocaleString()} tokens
                      </Text>
                    )}
                  </Flex>
                )}
              </Box>


              {/* Sidebar: Progress Bars + Flair Content */}
              <Box w={{ base: '100%', xl: '340px' }} flexShrink={0}>
                <VStack spacing={3} align="stretch">
                  {metrics?.progressBars?.difficulty && (
                    <InsightProgressBar label="Difficulty Adj" progress={metrics.progressBars.difficulty.progress} detail={metrics.progressBars.difficulty.detail} colorMode={colorMode} />
                  )}
                  {metrics?.progressBars?.halving && (
                    <InsightProgressBar label="Next Halving" progress={metrics.progressBars.halving.progress} detail={metrics.progressBars.halving.detail} colorMode={colorMode} />
                  )}
                  <SatoshiQuoteFlair colorMode={colorMode} />
                  <LexiconTermFlair colorMode={colorMode} />
                </VStack>
              </Box>
            </Flex>
          </ModalBody>

          {/* Provider Info Footer */}
          <HStack
            px={{ base: 4, md: 6, lg: 8 }}
            py={2}
            borderTopWidth="1px"
            borderColor={colorMode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
            fontSize="11px"
            color={secondaryText}
            justify="center"
            spacing={4}
            flexWrap="wrap"
            flexShrink={0}
          >
            <Text>{getValidConfigs().length} provider{getValidConfigs().length !== 1 ? 's' : ''}</Text>
            {currentInsight?.provider ? (
              <>
                <Text>•</Text>
                <Text>{currentInsight.model || currentInsight.provider}</Text>
                <Text>•</Text>
                <Text>via <Text as="span" color={THEME_COLORS.orange[500]}>{currentInsight.provider}</Text>{currentInsight.usedFallback && <Text as="span" color={THEME_COLORS.gray[300]}> (fallback)</Text>}</Text>
                {blocksStale > 0 && (
                  <>
                    <Text>•</Text>
                    <Text color={THEME_COLORS.orange[700]}>
                      {blocksStale} block{blocksStale !== 1 ? 's' : ''} old
                    </Text>
                  </>
                )}
              </>
            ) : estimatedCost && (
              <>
                <Text>•</Text>
                <Text>{estimatedCost.model}</Text>
                <Text>•</Text>
                <Text color={estimatedCost.isFree ? THEME_COLORS.green[500] : undefined}>
                  ~{((estimatedCost.inputTokens || 0) + (estimatedCost.outputTokens || 0)).toLocaleString()} tokens
                </Text>
              </>
            )}
          </HStack>
        </ModalContent>
      </Modal>

      {/* Intro onboarding modal — overlays the full-screen AI modal */}
      <Modal isOpen={showIntro} onClose={dismissIntro} size="md" isCentered>
        <ModalOverlay backgroundColor="rgba(0, 0, 0, 0.6)" />
        <ModalContent bg={colorMode === 'dark' ? THEME_COLORS.primary[500] : '#ffffff'} borderRadius="3px">
          <ModalHeader pb={2}>
            <HStack spacing={3}>
              <Box bg={THEME_COLORS.orange[500]} p={2} borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                <FontAwesomeIcon icon={faRobot} color="#ffffff" />
              </Box>
              <Text fontSize="18px" fontWeight="700" color={colorMode === 'dark' ? '#ffffff' : '#121212'}>Timechain Stats AI</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color={colorMode === 'dark' ? THEME_COLORS.gray[200] : THEME_COLORS.gray[300]} />
          <ModalBody pt={0} pb={2}>
            <List spacing={3}>
              {[
                { icon: faCog, text: 'Configure your preferred AI provider via the gear icon' },
                { icon: faLightbulb, text: 'Click "Analyze Network" to generate AI-powered Bitcoin analysis' },
                { icon: faGlobe, text: 'Analysis covers Market, Network, Mining, Lightning, and Activity' },
                { icon: faCheckCircle, text: 'You provide your own API key — TimechainStats never sees or stores your keys' },
              ].map((item, idx) => (
                <ListItem key={idx} display="flex" alignItems="flex-start" fontSize="14px" color={colorMode === 'dark' ? THEME_COLORS.gray[100] : THEME_COLORS.gray[300]}>
                  <ListIcon as={FontAwesomeIcon} icon={item.icon} color={THEME_COLORS.orange[500]} mt="3px" />
                  {item.text}
                </ListItem>
              ))}
            </List>
          </ModalBody>
          <ModalFooter pt={2}>
            <Button bg={THEME_COLORS.orange[500]} color="#ffffff" _hover={{ bg: THEME_COLORS.orange[300] }} onClick={dismissIntro} w="100%">
              Got it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AIInsightsButton;
